import type { ChangeEvent, FC } from 'react'
import { useState } from 'react'

import { InfoBox, Input, MessagePopup, Select, Textarea, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { DECIMAL_PATTERN, getPricePrecision, MAX_DECIMAL_12_4 } from '@skytech/utils'
import { sanitizeAndInsertTargetBlank } from '@skytech/utils/html-sanitization'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field } from 'formik'
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon'
import CalculatorVariantOutlineIcon from 'mdi-react/CalculatorVariantOutlineIcon'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import StoreRemoveIcon from 'mdi-react/StoreRemoveIcon'
import { useParams, useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useHasAssetEditingAccess, useHasFeatureActivationAccess } from '@/hooks/access'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import adminQueries from '@/modules/admin/queries'
import type { AssetExtended } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { useFeatureCustomers } from '@/modules/features'
import type { Policy } from '@/resources/ecom-policy'
import { leasingResources } from '@/resources/leasing'
import { APP_ROUTES } from '@/util/app-routes'

import { formattedCostValue } from '../helpers'
import { getIneligibilityReason, isAssetEligibleForSale } from '../helpers/saleEligibility'
import type { FormFields } from '../types'

import { SellAssetModal } from './SellAssetModal'

import { getSelectOptions } from '../../helpers'

// The only way to check the ownership type
const OWNED_TYPE_ID = -1

export const Asset: FC<{
  assetDetails: AssetExtended
  activePolicies: Policy[]
  onSetFormValues: (updatedValues: Partial<FormFields>) => void
  onChangeUserTypeId: (userTypeId: number) => void
  isFormDisabled?: boolean
}> = ({
  activePolicies,
  assetDetails,
  onSetFormValues,
  onChangeUserTypeId,
  isFormDisabled = false,
}) => {
  const hasEditAccess = useHasAssetEditingAccess()

  const translate = useTranslation()
  const params = useParams()
  const profile = useProfile()
  const assetId = Number(params.id)
  const hasFeatureActivationAccess = useHasFeatureActivationAccess()
  const { data: internalSalesCustomers } = useFeatureCustomers('internal_sales')

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))
  if (!profile) throw new Error('No profile found for Asset!')

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isAddAssetToTradeInPopupOpen, setIsAddAssetToTradeInPopupOpen] = useState(false)
  const [isSellAssetModalOpen, setIsSellAssetModalOpen] = useState(false)
  const [isInternalSalesDetailsOpen, setIsInternalSalesDetailsOpen] = useState(false)
  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  const { data: leasingInfo } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: Boolean(assetDetails) && assetDetails.isLeased }
  )

  // Check if the asset's customer has internal_sales feature activated
  const assetCustomer = internalSalesCustomers?.find(
    customer => customer.customerId === assetDetails.customerId
  )
  const isInternalSalesActiveForCustomer = assetCustomer?.active ?? false

  const { data: internalSalesFeatureDetails } = useQuery(
    adminQueries.adminFeatureDetailsByCode('internal_sales'),
    () => adminResources.getFeatureDetailsByCode('internal_sales'),
    {
      enabled: !profile.featureAccess.internal_sales || !isInternalSalesActiveForCustomer,
    }
  )

  const sendPriceRequestMutation = useMutation(adminResources.sendTradeInPriceRequest, {
    onSuccess() {
      setIsAddAssetToTradeInPopupOpen(false)
      queryClient.invalidateQueries(
        adminQueries.adminAssetTradeInOrder(assetDetails.assetGuid ?? '')
      )
    },
  })

  function handleAddToGreenpool() {
    const statusId = assetTypes?.statuses.find(status => status.status === 'AVAILABLE')?.id
    const userTypeId = assetTypes?.userTypes.find(type => type.type === 'GREENPOOL')?.id
    if (!statusId) throw Error('Status id not found!')
    if (!userTypeId) throw Error('User type id not found!')
    onSetFormValues({ statusId, userTypeId })
    onChangeUserTypeId(userTypeId)
  }

  function handleAddToPool() {
    const statusId = assetTypes?.statuses.find(status => status.status === 'AVAILABLE')?.id
    const userTypeId = assetTypes?.userTypes.find(type => type.type === 'POOL')?.id
    if (!statusId) throw Error('Status id not found!')
    if (!userTypeId) throw Error('User type id not found!')
    onSetFormValues({ statusId, userTypeId })
    onChangeUserTypeId(userTypeId)
  }

  const handleCostChange = (e: ChangeEvent<HTMLInputElement>, form: FormikProps<FormFields>) => {
    let newValue = e.target.value.trim()
    if (!DECIMAL_PATTERN.test(newValue) && newValue !== '') return
    form.setFieldValue('cost', newValue)
  }

  const isEcomPoliciesAvailable = activePolicies.length > 0
  const lockAssetType =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetType || leasingInfo?.lockFields?.includes('typeId'))
  const lockAssetDescription =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetDescription ||
      leasingInfo?.lockFields?.includes('assetDescription'))
  const lockAssetOwnership =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetOwnership ||
      leasingInfo?.lockFields?.includes('ownershipId'))
  const lockAssetImeiSerialNumber =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.imeiSerialNumber ||
      leasingInfo?.lockFields?.includes('imeiSerialNumber'))
  const showCostNote = assetDetails.cost === 0 || assetDetails.cost === null

  // Trade-in
  const isTradeInEnabled = Boolean(profile.featureAccess.tradein)
  const { data: featuresList } = useQuery(queries.adminFeatures(), adminResources.getFeatureList, {
    enabled: isTradeInEnabled,
  })
  const tradeInFeature = featuresList?.find(feature => feature.name === 'tradein')
  const customerFeatures = tradeInFeature?.customerFeatures.find(
    feature => feature.customerId === assetDetails.customerId
  )
  const isTradeInFutureAvailableForCustomer = Boolean(customerFeatures)

  const { data: tradeInOrder, isLoading: isTradeInOrderLoading } = useQuery(
    adminQueries.adminAssetTradeInOrder(assetDetails.assetGuid ?? ''),
    () => {
      if (!assetDetails.assetGuid) throw Error('Asset guid missing!')
      return adminResources.getTradeInAssetOrder(assetDetails.assetGuid)
    },
    { enabled: isTradeInEnabled && Boolean(tradeInFeature) }
  )

  const isAssetOwned = assetDetails.ownershipTypeId === OWNED_TYPE_ID
  const isTradInBtnEnabled = isAssetOwned && isTradeInEnabled

  // Check if asset is eligible for internal sale
  const isEligibleForSale = isAssetEligibleForSale(assetDetails)
  const ineligibilityReason = !isEligibleForSale ? getIneligibilityReason(assetDetails) : undefined

  // Check if there's an active buyout offer
  const { data: activeBuyoutOffer } = useQuery(
    queries.adminActiveBuyoutOfferByAsset(assetId),
    () => adminResources.getActiveBuyoutOfferByAsset(assetId),
    { enabled: Boolean(assetId) }
  )

  // Checks if an order has been rejected to make a new request. The newest oder is first.
  const isOrderRejected = Boolean(
    tradeInOrder && tradeInOrder.length > 0 && tradeInOrder[0].statusCode === 'REJECTED'
  )
  const isTradeInProcessOngoing =
    Boolean(tradeInOrder && tradeInOrder.length > 0) && !isOrderRejected

  function handleInitiateTradeIn() {
    if (!isTradeInFutureAvailableForCustomer) {
      const searchParams = new URLSearchParams()
      searchParams.set('selectedFeatureId', String(tradeInFeature?.id))
      searchParams.set('selectedCustomerId', String(assetDetails?.customerId))
      const search = String(searchParams) ? `?${searchParams}` : ''
      navigate(`${APP_ROUTES.ADMIN_FEATURES}${search}`)
      return
    }

    if (!isTradeInProcessOngoing) {
      setIsAddAssetToTradeInPopupOpen(true)
      return
    }

    if (!tradeInOrder?.[0]) throw Error('Trade in order missing!')

    navigate(`${APP_ROUTES.HARDWARE_TRADE_IN}?status=ALL`)
  }

  const onSellButtonClickHandler = () => {
    if (profile.featureAccess.internal_sales && isInternalSalesActiveForCustomer) {
      setIsSellAssetModalOpen(true)
    } else {
      setIsInternalSalesDetailsOpen(true)
    }
  }

  return (
    <>
      <div className="form-column-set">
        <div className="form-column">
          <Field name="assetDescription">
            {({ field, meta }: FieldProps) => (
              <Input
                capitalizeLabel
                isFullWidth
                id="assetDescription"
                className="description-input"
                ariaLabel={translate('Description')}
                label={
                  <>
                    <Translation id="Description" />*
                  </>
                }
                isDisabled={lockAssetDescription || !hasEditAccess || isFormDisabled}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>

          <div className="flex items-center gap-1">
            <Field name="ownershipTypeId">
              {({ field, form }: FieldProps) => (
                <Select
                  capitalizeLabel
                  id="ownershipTypeId"
                  label={
                    <>
                      <Translation id="Ownership" />*
                    </>
                  }
                  options={
                    getSelectOptions(
                      assetTypes?.ownerships.map(({ id, textId, name }) => ({
                        id,
                        label: name ? { id: textId, values: { name } } : textId,
                      })),
                      translate
                    ) || []
                  }
                  isDisabled={lockAssetOwnership || !hasEditAccess || isFormDisabled}
                  marginBottom={16}
                  {...field}
                  onChange={option => {
                    if (lockAssetOwnership || isFormDisabled) return
                    form.setFieldValue(field.name, option?.value)
                  }}
                />
              )}
            </Field>

            <Field name="typeId">
              {({ field, form }: FieldProps) => (
                <Select
                  capitalizeLabel
                  className="grow"
                  id="typeId"
                  label={
                    <>
                      <Translation id="Type" />*
                    </>
                  }
                  options={
                    assetTypes?.types.map(({ id, type }) => ({
                      value: id,
                      label: translate(`asset_type/${type}`),
                    })) || []
                  }
                  isDisabled={lockAssetType || !hasEditAccess || isFormDisabled}
                  marginBottom={16}
                  {...field}
                  onChange={option => {
                    if (lockAssetType || isFormDisabled) return
                    form.setFieldValue(field.name, option?.value)
                  }}
                />
              )}
            </Field>
          </div>

          <Field name="ecomPolicyId">
            {({ field, form, meta }: FieldProps) => (
              <Select
                capitalizeLabel
                id="ecomPolicyId"
                label={
                  <>
                    <Translation id="Ecom policy" />
                    {isEcomPoliciesAvailable && '*'}
                  </>
                }
                options={activePolicies.map(policy => ({
                  value: policy.id,
                  label: policy.name,
                }))}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
                onChange={option => {
                  if (isFormDisabled) return
                  form.setFieldValue(field.name, option?.value)
                }}
                isDisabled={!hasEditAccess || isFormDisabled}
              />
            )}
          </Field>
        </div>

        <div className="form-column">
          <div className="two-fields flex flex-wrap items-center gap-1">
            <Field name="imei">
              {({ field, meta }: FieldProps) => (
                <Input
                  capitalizeLabel
                  isFullWidth
                  id="imei"
                  ariaLabel={translate('IMEI')}
                  label={<Translation id="IMEI" />}
                  isDisabled={lockAssetImeiSerialNumber || !hasEditAccess || isFormDisabled}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                />
              )}
            </Field>
            <Field name="snr">
              {({ field, meta }: FieldProps) => (
                <Input
                  capitalizeLabel
                  isFullWidth
                  id="snr"
                  ariaLabel={translate('Serialnbr')}
                  label={<Translation id="Serialnbr" />}
                  isDisabled={lockAssetImeiSerialNumber || !hasEditAccess || isFormDisabled}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                />
              )}
            </Field>
          </div>

          {showCostNote && (
            <InfoBox marginBottom={4} emphasis="warning">
              <div className="flex items-center gap-1">
                <AlertCircleOutlineIcon className="w-auto self-start" />
                <h4 className="m-0">
                  <Translation id="Asset cost currently unknown / not provided by supplier." />
                </h4>
              </div>
            </InfoBox>
          )}

          <div className="flex gap-1 [&>*]:flex-1">
            <Field name="received">
              {({ field, meta }: FieldProps) => (
                <Input
                  capitalizeLabel
                  isFullWidth
                  id="received"
                  type="date"
                  ariaLabel={translate('Received')}
                  label={<Translation id="Received" />}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                  isDisabled={!hasEditAccess || isFormDisabled}
                />
              )}
            </Field>

            <Field name="cost">
              {({ field, meta, form }: FieldProps) => (
                <div className="flex gap-1">
                  <Input
                    capitalizeLabel
                    isFullWidth
                    id="cost"
                    type="number"
                    inputMode="decimal"
                    max={String(MAX_DECIMAL_12_4)}
                    pattern="\d*"
                    placeholder={field.value ? undefined : translate('No cost set')}
                    step="0.01"
                    className="min-w-max"
                    LeftElement={
                      Number(field.value) > 0 ? (
                        <span>{assetDetails.assetCurrency || profile?.currency}</span>
                      ) : undefined
                    }
                    ariaLabel={translate('Cost')}
                    label={<Translation id="Cost" />}
                    marginBottom={16}
                    touched={meta.touched}
                    error={meta.error}
                    {...field}
                    value={field.value}
                    onChange={e => handleCostChange(e, form)}
                    onBlur={e => {
                      field.onBlur(e)
                      const newValue =
                        Number(field.value) === 0 ? '' : formattedCostValue(Number(field.value))
                      form.setFieldValue('cost', newValue)
                    }}
                    isDisabled={!hasEditAccess || isFormDisabled}
                  />

                  {assetDetails.priceBuyout != null &&
                    assetDetails.priceBuyout > 0 &&
                    !assetDetails.isLeased && (
                      <div className="mb-4 flex-1">
                        <div className="flex h-full w-full flex-col items-center gap-1 rounded border border-neutral-300 px-3 text-base text-neutral-900">
                          {/* Font color for p 7f8c8d */}
                          <p className="mb-0 mt-6 text-sm text-neutral-400">
                            <Translation id="Buyout price:" className="mt-5" />
                          </p>
                          <FormattedNumberWithCurrency
                            amount={assetDetails.priceBuyout || 0}
                            precision={getPricePrecision(assetDetails.priceBuyout || 0)}
                            currency={assetDetails.assetCurrency}
                          />
                        </div>
                      </div>
                    )}
                </div>
              )}
            </Field>
          </div>
          <div className="w-full">
            <Field name="conditionDesc">
              {({ field, meta }: FieldProps) => (
                <Textarea
                  className="resize-y"
                  capitalizeLabel
                  isFullWidth
                  id="conditionDesc"
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  maxLength={250}
                  label={<Translation id="Condition of the asset (visible in GreenPool)" />}
                  // Hack to calculate the number of rows based on the length of the value
                  rows={Math.floor(field.value.length / 60) + 1}
                  {...field}
                  isDisabled={!hasEditAccess || isFormDisabled}
                />
              )}
            </Field>
          </div>

          {hasEditAccess && (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-1">
              <Button
                className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
                Icon={<RecycleIcon />}
                onClick={handleAddToGreenpool}
                disabled={isFormDisabled}
              >
                <Translation id="Add to greenpool" className="text-wrap" />
              </Button>
              <Button
                className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
                Icon={<PailOutlineIcon />}
                onClick={handleAddToPool}
                disabled={isFormDisabled}
              >
                <Translation id="Add to pool" />
              </Button>
              {(profile.featureAccess.internal_sales || hasFeatureActivationAccess) && (
                <div id="ineligibilityReason">
                  <Button
                    disabled={!isEligibleForSale || Boolean(activeBuyoutOffer) || isFormDisabled}
                    className="m-0 flex h-auto w-full flex-col justify-center gap-1 px-3 py-4"
                    Icon={<StoreRemoveIcon />}
                    onClick={onSellButtonClickHandler}
                  >
                    <Translation id="Sell" />
                    <Tooltip
                      anchorId="ineligibilityReason"
                      size="large"
                      content={ineligibilityReason}
                    />
                  </Button>
                </div>
              )}

              {isTradInBtnEnabled && (
                <Button
                  className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
                  isLoading={isTradeInOrderLoading}
                  Icon={<CalculatorVariantOutlineIcon />}
                  onClick={handleInitiateTradeIn}
                  disabled={isFormDisabled}
                >
                  <Translation id={isTradeInProcessOngoing ? 'Trade-in order' : 'Request price'} />
                </Button>
              )}

              <MessagePopup
                isOpen={isAddAssetToTradeInPopupOpen}
                title={<Translation id="Add asset" />}
                // TODO: Change the text according to the requirements
                message={
                  <div className="flex flex-col gap-1">
                    <Translation id="The price is based on B-grade. For A-grade, prices are 20% higher, and for C-grade, prices are 20% lower." />
                    <Translation id="Do you want to proceed?" />
                  </div>
                }
              >
                <footer className="flex justify-end">
                  <Button variant="tertiary" onClick={() => setIsAddAssetToTradeInPopupOpen(false)}>
                    <Translation id="Cancel" />
                  </Button>
                  <Button
                    isLoading={sendPriceRequestMutation.isLoading}
                    onClick={() => {
                      if (!assetDetails.assetGuid) throw Error('Missing asset guid!')
                      sendPriceRequestMutation.mutate({
                        assetId: assetDetails.assetGuid,
                      })
                    }}
                  >
                    <Translation id="Send request" />
                  </Button>
                </footer>
              </MessagePopup>
            </div>
          )}
        </div>
      </div>

      {isSellAssetModalOpen && (
        <SellAssetModal
          assetDetails={assetDetails}
          isOpen={isSellAssetModalOpen}
          onClose={() => setIsSellAssetModalOpen(false)}
        />
      )}

      {isInternalSalesDetailsOpen && internalSalesFeatureDetails && (
        <MessagePopup
          title={
            <Translation
              id="Do you want to activate: {name}"
              name={internalSalesFeatureDetails.name}
            />
          }
          isOpen={isInternalSalesDetailsOpen}
          onClose={() => setIsInternalSalesDetailsOpen(false)}
          type="success"
          message={
            <div className="flex flex-col gap-2">
              {assetCustomer && (
                <div className="mt-2 rounded border border-blue-200 bg-blue-50 p-3">
                  <p className="m-0 font-semibold">
                    <Translation id="Customer:" /> {assetCustomer.name}
                  </p>
                  <p className="m-0 text-sm text-neutral-600">
                    <Translation id="This feature needs to be activated for this customer to enable selling this asset." />
                  </p>
                </div>
              )}
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeAndInsertTargetBlank(internalSalesFeatureDetails.description),
                }}
              />
            </div>
          }
        >
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={() => setIsInternalSalesDetailsOpen(false)}>
              <Translation id="Later" />
            </Button>

            <Button
              variant="primary"
              onClick={() => {
                const searchParams = new URLSearchParams()
                searchParams.set('selectedFeatureId', String(internalSalesFeatureDetails.id))
                if (assetCustomer) {
                  searchParams.set('selectedCustomerId', String(assetCustomer.customerId))
                }
                navigate(`${APP_ROUTES.ADMIN_FEATURES}?${searchParams.toString()}`)
              }}
            >
              <Translation id="Go to activation" />
            </Button>
          </div>
        </MessagePopup>
      )}
    </>
  )
}
